import { supabase } from './supabase';

export interface Permissions {
  topLevel: {
    homeScreenAppId?: string | null;
    canViewAppSettings?: boolean;
    nexusConnect?: boolean;
  };
  calendar: {
    pageAccess: boolean;
    sales?: boolean;
    viewAllBookings?: boolean;
    bookingInformation?: boolean;
    bookingSalesInfo?: boolean;
    editBookingDetails?: boolean;
    bookingMaterials?: boolean;
    bookingRatebook?: boolean;
    bookingProfitLoss?: boolean;
    bookingLiabilityWaiver?: boolean;
    createBookings?: boolean;
    defaultView?: 'month' | 'week' | 'day' | null;
    monthView?: boolean;
    weekView?: boolean;
    dayView?: boolean;
    // "Hide" toggles (enabled = hidden). Default off so behavior is unchanged.
    hideBookingList?: boolean;
    hideSearchBar?: boolean;
    hideUnscheduledTab?: boolean;
  };
  messages: {
    pageAccess: boolean;
  };
  marketing: {
    pageAccess: boolean;
  };
  campaigns: {
    pageAccess: boolean;
  };
  salesMap: {
    pageAccess: boolean;
  };
  crm: {
    pageAccess: boolean;
  };
  payroll: {
    pageAccess: boolean;
  };
  accountingHub: {
    pageAccess: boolean;
  };
  invoiceSettings: {
    pageAccess: boolean;
  };
  supplies: {
    pageAccess: boolean;
  };
  services: {
    pageAccess: boolean;
  };
  resources: {
    pageAccess: boolean;
  };
  salesReports: {
    pageAccess: boolean;
  };
  teamReports: {
    pageAccess: boolean;
  };
  suppliesUsage: {
    pageAccess: boolean;
  };
  settings: {
    pageAccess: boolean;
  };
  tasks: {
    pageAccess: boolean;
    // Can use the "Complete Document" quick-add option — independent of app access.
    canComplete?: boolean;
  };
  bookings: {
    pageAccess: boolean;
  };
  proposals: {
    pageAccess: boolean;
  };
  customers: {
    pageAccess: boolean;
  };
  kpiDashboard: {
    pageAccess: boolean;
  };
  automations: {
    pageAccess: boolean;
  };
  invoices: {
    pageAccess: boolean;
  };
  products: {
    pageAccess: boolean;
  };
  giftCards: {
    pageAccess: boolean;
  };
  inventory: {
    pageAccess: boolean;
  };
  projects: {
    pageAccess: boolean;
  };
  expenses: {
    pageAccess: boolean;
    appVisibility?: boolean;
    uploadExpense?: boolean;
  };
  queApp: {
    pageAccess: boolean;
    viewBuilder: boolean;
  };
  // Push notification delivery for resources assigned to this role.
  // A type is delivered only when explicitly true. Missing block / unset type = off.
  // Admins and role-less accounts ignore this and always receive every type.
  notifications?: Partial<Record<NotificationTypeKey, boolean>>;
}

// Push notification types. Keys map 1:1 to the notificationType values used by
// the send-push-notification edge function. Used to drive the role editor UI.
export type NotificationTypeKey =
  | 'sms'
  | 'email'
  | 'call'
  | 'proposal'
  | 'crm'
  | 'booking'
  | 'booking_status'
  | 'order'
  | 'timeoff'
  | 'clock_in'
  | 'break_start'
  | 'break_end'
  | 'clock_out'
  | 'payroll_request';

export const NOTIFICATION_TYPES: { key: NotificationTypeKey; label: string }[] = [
  { key: 'sms', label: 'New SMS messages' },
  { key: 'email', label: 'New emails' },
  { key: 'call', label: 'Incoming calls' },
  { key: 'proposal', label: 'Proposal activity' },
  { key: 'crm', label: 'New leads (CRM)' },
  { key: 'booking', label: 'New bookings' },
  { key: 'booking_status', label: 'Booking status changes' },
  { key: 'order', label: 'New customer orders' },
  { key: 'timeoff', label: 'Time off requests' },
  { key: 'clock_in', label: 'Clock in' },
  { key: 'break_start', label: 'Break start' },
  { key: 'break_end', label: 'Break end' },
  { key: 'clock_out', label: 'Clock out' },
  { key: 'payroll_request', label: 'Payroll requests' },
];

export interface ResourceRole {
  id: string;
  business_id: string;
  name: string;
  description: string;
  permissions: Permissions;
  created_at: string;
  updated_at: string;
}

export interface CreateRolePayload {
  name: string;
  description: string;
  business_id: string;
  permissions?: Partial<Permissions>;
}

// Get all roles for a business
export async function getResourceRoles(businessId: string): Promise<ResourceRole[]> {
  const { data, error } = await supabase
    .from('resource_roles')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Create a new role
export async function createResourceRole(payload: CreateRolePayload): Promise<ResourceRole> {
  // Default permissions structure (all false)
  const defaultPermissions: Permissions = {
    topLevel: {
      homeScreenAppId: null,
      canViewAppSettings: false,
      nexusConnect: false,
    },
    calendar: {
      pageAccess: false,
      sales: true,
      viewAllBookings: true,
      bookingInformation: true,
      bookingSalesInfo: true,
      editBookingDetails: true,
      bookingMaterials: true,
      bookingRatebook: true,
      bookingProfitLoss: true,
      bookingLiabilityWaiver: true,
      defaultView: null,
      monthView: true,
      weekView: true,
      dayView: true,
      hideBookingList: false,
      hideSearchBar: false,
      hideUnscheduledTab: false,
    },
    messages: {
      pageAccess: true,
    },
    marketing: {
      pageAccess: true,
    },
    campaigns: {
      pageAccess: true,
    },
    salesMap: {
      pageAccess: true,
    },
    crm: {
      pageAccess: true,
    },
    payroll: {
      pageAccess: true,
    },
    accountingHub: {
      pageAccess: true,
    },
    invoiceSettings: {
      pageAccess: true,
    },
    supplies: {
      pageAccess: false,
    },
    services: {
      pageAccess: false,
    },
    resources: {
      pageAccess: false,
    },
    salesReports: {
      pageAccess: false,
    },
    teamReports: {
      pageAccess: false,
    },
    suppliesUsage: {
      pageAccess: false,
    },
    settings: {
      pageAccess: false,
    },
    tasks: {
      pageAccess: false,
      canComplete: true,
    },
    bookings: {
      pageAccess: false,
    },
    proposals: {
      pageAccess: false,
    },
    customers: {
      pageAccess: false,
    },
    kpiDashboard: {
      pageAccess: false,
    },
    automations: {
      pageAccess: false,
    },
    invoices: {
      pageAccess: false,
    },
    products: {
      pageAccess: false,
    },
    giftCards: {
      pageAccess: false,
    },
    inventory: {
      pageAccess: false,
    },
    projects: {
      pageAccess: false,
    },
    queApp: {
      pageAccess: false,
      viewBuilder: false,
    },
    // All notification types off by default — admins opt a role in per type.
    notifications: {
      sms: false,
      email: false,
      call: false,
      proposal: false,
      crm: false,
      booking: false,
      booking_status: false,
      order: false,
      timeoff: false,
      clock_in: false,
      break_start: false,
      break_end: false,
      clock_out: false,
      payroll_request: false,
    },
  };

  const { data, error } = await supabase
    .from('resource_roles')
    .insert([{
      business_id: payload.business_id,
      name: payload.name,
      description: payload.description,
      // Always provide permissions - merge with defaults if provided
      permissions: { ...defaultPermissions, ...payload.permissions }
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Update a role
export async function updateResourceRole(
  roleId: string, 
  updates: Partial<Pick<ResourceRole, 'name' | 'description' | 'permissions'>>
): Promise<ResourceRole> {
  const { data, error } = await supabase
    .from('resource_roles')
    .update(updates)
    .eq('id', roleId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Delete a role
export async function deleteResourceRole(roleId: string): Promise<void> {
  const { error } = await supabase
    .from('resource_roles')
    .delete()
    .eq('id', roleId);

  if (error) throw error;
}

// Get role by ID
export async function getResourceRole(roleId: string): Promise<ResourceRole> {
  const { data, error } = await supabase
    .from('resource_roles')
    .select('*')
    .eq('id', roleId)
    .single();

  if (error) throw error;
  return data;
}

// Assign role to resource
export async function assignRoleToResource(resourceId: string, roleId: string | null): Promise<void> {
  const { error } = await supabase
    .from('resources')
    .update({ role_id: roleId })
    .eq('id', resourceId);

  if (error) throw error;
}

// Get resources with their assigned roles
export async function getResourcesWithRoles(businessId: string) {
  const { data, error } = await supabase
    .from('resources')
    .select(`
      *,
      role:resource_roles(*)
    `)
    .eq('business_id', businessId);

  if (error) throw error;
  return data;
} 